import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Copy, Check, Terminal } from 'lucide-react';
import { triggerHaptic } from '../services/purrSynthesizer';

interface ToolItem {
  name: string;
  args?: Record<string, any>;
  output: string | Record<string, any>;
  result?: any;
}

interface ToolUsageAccordionProps {
  tools: ToolItem[];
  isFamily?: boolean;
}

const FRIENDLY_TOOL_NAMES: Record<string, string> = {
  immich_photo_vault_info: 'Checked Photo Vault',
  family_notes_add: "Saved Note to Echo's Desk",
  family_notes_get: "Read Notes from Echo's Desk",
  fix_household_glitch: 'Household Self-Heal',
  tuya_lights_control: 'Adjusted Smart Lighting',
  frigate_events: 'Checked Castle Cameras',
  consult_fido_retriever: 'Consulted Fido Tool Hound',
  taproom_status: 'Checked Moonjoy Taproom',
  qdrant_query: 'Searched Neural Memory Bank',
  get_equity_quotes: 'Fetched Market Quotes',
  usb_device_status: 'Probed USB OTG Hardware',
  usb_serial_send_and_listen: 'Executed USB Serial Command',
  bastet_sensor_snapshot: 'Captured Sensor & EMF Snapshot',
  bastet_nfc_read: 'Interrogated NFC Transponder',
  bastet_hardware_status: 'Audited Bastet Sovereign HAL',
};

function formatToolLabel(name: string): string {
  if (FRIENDLY_TOOL_NAMES[name]) {
    return FRIENDLY_TOOL_NAMES[name];
  }
  return name
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export const ToolUsageAccordion: React.FC<ToolUsageAccordionProps> = ({ tools, isFamily }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);

  if (!tools || tools.length === 0) return null;

  const toggleAccordion = () => {
    triggerHaptic(15);
    setIsOpen((prev) => !prev);
  };

  const handleCopyJson = (content: any, idx: number, e: React.MouseEvent) => {
    e.stopPropagation();
    const str = typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content);
    navigator.clipboard.writeText(str);
    setCopiedIdx(idx);
    triggerHaptic(20);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  // Build a concise headline summary
  const summaryText =
    tools.length === 1
      ? formatToolLabel(tools[0].name)
      : `${tools.length} Homelab Actions Executed`;

  return (
    <div className="mb-2.5 rounded-xl border border-white/10 bg-black/40 overflow-hidden transition-all shadow-sm">
      {/* Collapsed Header Bar */}
      <div
        onClick={toggleAccordion}
        className={`px-3 py-2 flex items-center justify-between cursor-pointer transition-colors select-none ${
          isFamily ? 'hover:bg-emerald-950/20' : 'hover:bg-amber-950/20'
        } ${isOpen ? 'border-b border-white/10 bg-white/5' : ''}`}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs shrink-0">🐾</span>
          <div className="flex items-center gap-1.5 truncate">
            <span className={`text-[11px] font-semibold truncate ${
              isFamily ? 'text-emerald-300' : 'text-fissal-goldLight'
            }`}>
              {summaryText}
            </span>
            <span className="inline-flex items-center px-1.5 py-0.2 rounded-full text-[9px] font-mono bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shrink-0">
              Success
            </span>
          </div>
        </div>

        <div className="flex items-center gap-1 text-[10px] font-mono text-slate-400 hover:text-slate-200 shrink-0 ml-2">
          <span>{isOpen ? 'Hide' : 'Details'}</span>
          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </div>
      </div>

      {/* Expanded Details Body */}
      {isOpen && (
        <div className="p-2.5 space-y-2 bg-black/60">
          {tools.map((t, idx) => {
            const outputData = t.output || t.result;
            return (
              <div
                key={idx}
                className="rounded-lg border border-white/10 bg-[#080d15] p-2 text-[10px] font-mono"
              >
                <div className="flex items-center justify-between mb-1 text-slate-400">
                  <div className="flex items-center gap-1.5 text-sky-300 font-semibold">
                    <Terminal className="w-3 h-3 text-fissal-gold" />
                    <span>{t.name}()</span>
                  </div>

                  <button
                    type="button"
                    onClick={(e) => handleCopyJson(outputData, idx, e)}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-all text-[9px]"
                    title="Copy JSON Payload"
                  >
                    {copiedIdx === idx ? (
                      <>
                        <Check className="w-2.5 h-2.5 text-emerald-400" />
                        <span className="text-emerald-400">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-2.5 h-2.5" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>

                {outputData && (
                  <pre className="mt-1 p-2 rounded bg-black/70 border border-white/5 text-[10px] leading-relaxed text-slate-300 max-h-36 overflow-y-auto overflow-x-hidden whitespace-pre-wrap selection:bg-fissal-gold/30 font-mono">
                    {typeof outputData === 'object'
                      ? JSON.stringify(outputData, null, 2)
                      : String(outputData)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
